import { useState } from "react";
import { Loader2 } from "lucide-react";

import { useAdminMetrics } from "@/hooks/queries/useAdminMetrics";
import {
  MetricCard,
  MetricsSection,
  DailyTrendChart,
  FunnelBars,
} from "@/components/admin/metrics/MetricsCharts";
import { formatDate, formatNumber } from "@/lib/format";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const RANGES = [
  { value: "7", label: "Últimos 7 días" },
  { value: "30", label: "Últimos 30 días" },
  { value: "90", label: "Últimos 90 días" },
];

const SERIES_COLOR = {
  signups: "#2563eb",
  activeUsers: "#8b5cf6",
  unlocks: "#10b981",
  cost: "#f59e0b",
  contractings: "#2563eb",
};

const usd = (value: number) => `$ ${value.toFixed(2)}`;

export default function AdminMetrics() {
  const [days, setDays] = useState("30");
  const { data, isLoading, isError } = useAdminMetrics(Number(days));

  return (
    <div className="mx-auto w-full max-w-[1600px] flex-1 px-4 py-6 md:px-6">
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Métricas</h1>
          <p className="text-sm text-muted-foreground">
            Estado del negocio: adquisición, activación, uso y costo operativo.
          </p>
        </div>
        <Select value={days} onValueChange={setDays}>
          <SelectTrigger className="sm:w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {RANGES.map((range) => (
              <SelectItem key={range.value} value={range.value}>
                {range.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex items-center justify-center py-24">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : isError || !data ? (
        <div className="rounded-2xl border bg-card py-16 text-center text-sm text-muted-foreground">
          No pudimos cargar las métricas.
        </div>
      ) : (
        <div className="flex flex-col gap-8">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <MetricCard
              label="Registros"
              value={formatNumber(data.funnel.registered)}
              hint={`en los últimos ${data.days} días`}
            />
            <MetricCard
              label="Con acceso vigente"
              value={formatNumber(data.funnel.activeSubscription)}
              hint="de esa cohorte"
              tone="good"
            />
            <MetricCard
              label="Activados"
              value={formatNumber(data.funnel.activated)}
              hint="desbloquearon ≥ 1 licitación"
            />
            <MetricCard
              label="Costo IA"
              value={usd(data.aiCost.totalUsd)}
              hint={`en los últimos ${data.days} días`}
              tone="warning"
            />
          </div>

          <MetricsSection
            title="Embudo de la cohorte"
            description={`Usuarios registrados en los últimos ${data.days} días y hasta dónde llegaron.`}
          >
            <FunnelBars
              steps={[
                { label: "Se registraron", value: data.funnel.registered },
                {
                  label: "Completaron el formulario",
                  value: data.funnel.formCompleted,
                },
                { label: "Aceptados en la beta", value: data.funnel.accepted },
                {
                  label: "Con suscripción vigente",
                  value: data.funnel.activeSubscription,
                },
                {
                  label: "Desbloquearon una licitación",
                  value: data.funnel.activated,
                },
              ]}
            />
          </MetricsSection>

          <MetricsSection title="Registros por día">
            <DailyTrendChart
              data={data.signupsPerDay}
              dataKey="count"
              color={SERIES_COLOR.signups}
              format={(v) => `${v} registros`}
            />
          </MetricsSection>

          <MetricsSection
            title="Uso"
            description="Un usuario cuenta como activo el día que gasta una vista desbloqueando una licitación."
          >
            <div className="grid gap-3 lg:grid-cols-2">
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">Usuarios activos por día</p>
                <DailyTrendChart
                  data={data.usage.activeUsersPerDay}
                  dataKey="count"
                  color={SERIES_COLOR.activeUsers}
                  format={(v) => `${v} usuarios`}
                />
              </div>
              <div className="flex flex-col gap-2">
                <p className="text-sm font-medium">Vistas gastadas por día</p>
                <DailyTrendChart
                  data={data.usage.unlocksPerDay}
                  dataKey="count"
                  color={SERIES_COLOR.unlocks}
                  format={(v) => `${v} vistas`}
                />
              </div>
            </div>

            {data.usage.topUsers.length > 0 && (
              <div className="rounded-2xl border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Usuario que más desbloquea</TableHead>
                      <TableHead className="text-right">Vistas</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.usage.topUsers.map((user) => (
                      <TableRow key={user.userId}>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-medium">{user.name}</span>
                            <span className="text-sm text-muted-foreground">
                              {user.email}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {user.unlocks}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </MetricsSection>

          <MetricsSection
            title="Suscripciones"
            description="Los que vencen pronto son a quienes hay que renovar antes de que pierdan el acceso."
          >
            <div className="grid gap-3 lg:grid-cols-2">
              <div className="rounded-2xl border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Plan</TableHead>
                      <TableHead className="text-right">Vigentes</TableHead>
                      <TableHead className="text-right">No vigentes</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.subscriptions.byPlan.map((row) => (
                      <TableRow key={row.planName}>
                        <TableCell className="font-medium">
                          {row.planName}
                        </TableCell>
                        <TableCell className="text-right tabular-nums">
                          {row.active}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-muted-foreground">
                          {row.inactive}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="rounded-2xl border bg-card">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Vence en los próximos 7 días</TableHead>
                      <TableHead className="text-right">Vence</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.subscriptions.expiringSoon.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={2}
                          className="py-8 text-center text-sm text-muted-foreground"
                        >
                          Nadie vence esta semana.
                        </TableCell>
                      </TableRow>
                    ) : (
                      data.subscriptions.expiringSoon.map((row) => (
                        <TableRow key={row.userId}>
                          <TableCell>
                            <div className="flex flex-col">
                              <span className="font-medium">{row.name}</span>
                              <span className="text-sm text-muted-foreground">
                                {row.email} · {row.planName}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-right text-sm">
                            {formatDate(row.expiresat)}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          </MetricsSection>

          <MetricsSection
            title="Costo de IA"
            description="Estimado a partir de los tokens consumidos en cada llamada."
          >
            <DailyTrendChart
              data={data.aiCost.perDay}
              dataKey="usd"
              color={SERIES_COLOR.cost}
              format={usd}
            />
            <div className="rounded-2xl border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Modelo</TableHead>
                    <TableHead className="text-right">Llamadas</TableHead>
                    <TableHead className="text-right">USD</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.aiCost.byModel.map((row) => (
                    <TableRow key={row.model}>
                      <TableCell className="font-medium">{row.model}</TableCell>
                      <TableCell className="text-right tabular-nums">
                        {formatNumber(row.calls)}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        {usd(row.usd)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </MetricsSection>

          <MetricsSection
            title="Contenido"
            description="Cuánta licitación nueva entra y cómo va el procesamiento de documentos."
          >
            <DailyTrendChart
              data={data.content.contractingsPerDay}
              dataKey="count"
              color={SERIES_COLOR.contractings}
              format={(v) => `${v} licitaciones`}
            />
            <div className="grid grid-cols-3 gap-3">
              <MetricCard
                label="Procesados OK"
                value={formatNumber(data.content.filesProcessed.ok)}
                tone="good"
              />
              <MetricCard
                label="Con error"
                value={formatNumber(data.content.filesProcessed.failed)}
                tone="critical"
              />
              <MetricCard
                label="Pendientes"
                value={formatNumber(data.content.filesProcessed.pending)}
                tone="warning"
              />
            </div>
          </MetricsSection>
        </div>
      )}
    </div>
  );
}
